import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/lib/constants";

const LINKS = [
  { href: "/#home", label: "Home" },
  { href: "/#services", label: "Services" },
  { href: "/#studio", label: "The Space" },
  { href: "/#about", label: "Coach" },
  { href: "/#contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-zinc-950/85 border-b border-zinc-800"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center">
          <img
            src={BRAND.logo}
            alt="JE Training — Strength, Togetherness"
            className="h-16 lg:h-20 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-display text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-[0.14em] uppercase"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/book"
            data-testid="header-book-link"
            className="inline-flex items-center border border-zinc-700 hover:border-[#D4AF37] text-white px-5 py-2.5 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
          >
            Book
          </Link>
          <a
            href="/#contact"
            data-testid="header-cta-button"
            className="inline-flex items-center bg-[#D4AF37] hover:bg-[#F3CE56] text-zinc-950 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300"
          >
            Enquire Now
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-xl"
        >
          <nav className="flex flex-col px-6 py-6 gap-5">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-display text-white text-xl font-bold tracking-[0.1em] uppercase"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-button"
              className="mt-2 inline-flex items-center justify-center bg-[#D4AF37] text-zinc-950 px-5 py-3 font-display text-sm font-bold uppercase tracking-[0.14em]"
            >
              Enquire Now
            </a>
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-button"
              className="inline-flex items-center justify-center border border-zinc-700 text-white px-5 py-3 font-display text-sm font-bold uppercase tracking-[0.14em]"
            >
              Book Session
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
